/**
 * AuthContext — frontend seansi kontekst.
 *
 * - Hoiab praegust kasutajat ({ username, role } või null kui pole sees logitud)
 * - Pakub login/logout meetodid (kutsuvad /api/auth/*)
 * - Globaalne fetch-wrap (`apiFetch`), mis 401 puhul tühjendab konteksti
 *
 * Backend kasutab httpOnly cookie'd, frontend pole vaja JWT't käsitleda.
 *
 * Copyright (c) 2025 Vimpla OÜ
 */
import { createContext, useContext, useState, useEffect, useCallback } from 'react';

// Superadmini vaate-tenant (null = kõik kliendid). Loetakse fetch-wrapperis, et
// lisada ?tenant=<id> API-kutsetele. Mooduli-tasandi muutuja, et wrapper näeks seda.
let _viewTenant = (() => { try { return localStorage.getItem('ems_view_tenant') || null; } catch { return null; } })();
export function setViewTenantGlobal(t) { _viewTenant = t || null; }
/** Valitud vaate-tenant (WebSocket URL-i jaoks, mis fetch-wrapperist läbi ei käi). */
export function getViewTenant() { return _viewTenant; }

const AuthContext = createContext({
  user: null,
  loading: true,
  login: async () => {},
  logout: async () => {},
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // /api/auth/me — kontrolli, kas seanss aktiivne pärast lehe avamist
  useEffect(() => {
    let cancelled = false;
    fetch('/api/auth/me', { credentials: 'same-origin' })
      .then(r => r.json().then(j => ({ ok: r.ok, j })))
      .then(({ ok, j }) => {
        if (cancelled) return;
        if (ok && j.authenticated) setUser(j.user);
        else setUser(null);
      })
      .catch(() => { if (!cancelled) setUser(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const login = useCallback(async (username, password) => {
    const res = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ username, password }),
    });
    const j = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(j.error || `HTTP ${res.status}`);
    setUser(j.user);
    return j.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await fetch('/api/auth/logout', { method: 'POST', credentials: 'same-origin' });
    } catch (_) {}
    setUser(null);
  }, []);

  // Multi-tenant mugavus-lipud (backend /me tagastab role + tenant_id + is_superadmin)
  const isSuperadmin = !!user?.is_superadmin;
  const isAdmin = user?.role === 'admin';          // Vimpla-tasandi admin (sh superadmin)
  const tenantId = user?.tenant_id || null;
  const canControl = isAdmin;                       // PCS/ak-juhtimine = admin+; klient (operator) ei saa
  const canConfig = isAdmin || user?.role === 'operator'; // klient saab oma parke seadistada
  // Uute parkide/seadmete lisamine: ainult superadmin (otsus 2026-10-16; hiljem plaanis ka
  // kliendi admin — muuda siin + backend lib/access.js canAddParksOrDevices)
  const canAddDevices = isSuperadmin;

  // Superadmini tenant-valija: null = kõik kliendid, muidu üks tenant
  const [viewTenant, setViewTenantState] = useState(() => {
    try { return localStorage.getItem('ems_view_tenant') || null; } catch { return null; }
  });
  const [viewTenantName] = useState(() => {
    try { return localStorage.getItem('ems_view_tenant_name') || null; } catch { return null; }
  });
  const setViewTenant = useCallback((t, name) => {
    try {
      if (t) {
        localStorage.setItem('ems_view_tenant', t);
        localStorage.setItem('ems_view_tenant_name', name || t);
      } else {
        localStorage.removeItem('ems_view_tenant');
        localStorage.removeItem('ems_view_tenant_name');
      }
    } catch (_) {}
    setViewTenantGlobal(t);
    setViewTenantState(t || null);
    window.location.reload(); // kindel refetch kõigile vaadetele uue filtriga
  }, []);

  return (
    <AuthContext.Provider value={{
      user, loading, login, logout, setUser,
      isSuperadmin, isAdmin, tenantId, canControl, canConfig, canAddDevices,
      viewTenant, viewTenantName, setViewTenant,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

/**
 * Globaalne 401 monitor — kui mõni fetch tagastab 401, on seanss aegunud.
 * Komponent paigaldab fetch'i wrappi mis kuulab 401 vastuseid.
 */
export function setupAuthFetchInterceptor(onUnauthorized) {
  const original = window.fetch;
  if (original.__ems_wrapped) return;
  const wrapped = async (...args) => {
    // Superadmini tenant-vaate-filter: lisa ?tenant=<id> KÕIGILE /api/ kutsetele (ka
    // POST/PUT/DELETE) — filtreeritud GET + filtrita salvestus kirjutaks muidu teiste
    // klientide andmed üle (nt parks.json: salvestus kustutaks vaatest väljas olevad pargid).
    // Ainult string-URL + mitte auth-rajad + mitte juba tenant-param olemas.
    if (_viewTenant && typeof args[0] === 'string') {
      const u = args[0];
      if (u.startsWith('/api/') && !u.startsWith('/api/auth/') && !/[?&]tenant=/.test(u)) {
        args[0] = u + (u.includes('?') ? '&' : '?') + 'tenant=' + encodeURIComponent(_viewTenant);
      }
    }
    const res = await original.apply(window, args);
    if (res.status === 401) {
      // Ainult /api/* päringute puhul — staatilist sisu ei tohi käsitleda
      const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';
      if (url.includes('/api/') && !url.includes('/api/auth/login')) {
        try { onUnauthorized?.(); } catch (_) {}
      }
    }
    return res;
  };
  wrapped.__ems_wrapped = true;
  window.fetch = wrapped;
}
